import type { BSplineDefinition, Vec3 } from './types.ts';
import { TrackBuildError } from './types.ts';
import { add, finite, scale, sub, vec } from './vector.ts';

interface CurveLevel {
  readonly points: readonly Vec3[];
  readonly knots: readonly number[];
  readonly degree: number;
}

export interface BSplineEvaluation {
  readonly position: Vec3;
  readonly derivative: Vec3;
  readonly secondDerivative: Vec3;
}

export interface BSplineSpan {
  readonly index: number;
  readonly uStart: number;
  readonly uEnd: number;
  readonly controlPointIndices: readonly number[];
}

function openUniformKnots(pointCount: number, degree: number): number[] {
  const knotCount = pointCount + degree + 1;
  const interiorCount = pointCount - degree - 1;
  return Array.from({ length: knotCount }, (_, index) => {
    if (index <= degree) return 0;
    if (index >= pointCount) return 1;
    return (index - degree) / (interiorCount + 1);
  });
}

function periodicUniformKnots(pointCount: number, degree: number): number[] {
  return Array.from({ length: pointCount + degree + 1 }, (_, index) => index);
}

function validateKnots(knots: readonly number[], pointCount: number, degree: number): void {
  if (knots.length !== pointCount + degree + 1) {
    throw new TrackBuildError(
      'invalid_knot_count',
      `Expected ${pointCount + degree + 1} knots, received ${knots.length}.`,
    );
  }
  for (let index = 0; index < knots.length; index += 1) {
    if (!Number.isFinite(knots[index]))
      throw new TrackBuildError('non_finite_knot', `Knot ${index} is not finite.`);
    if (index > 0 && knots[index] < knots[index - 1])
      throw new TrackBuildError('unordered_knots', 'Knots must be non-decreasing.');
  }
  if (!(knots[degree] < knots[pointCount]))
    throw new TrackBuildError('empty_curve_domain', 'The active knot domain has zero length.');
}

function findSpan(level: CurveLevel, u: number): number {
  const n = level.points.length - 1;
  if (u >= level.knots[n + 1]) return n;
  if (u <= level.knots[level.degree]) return level.degree;
  let low = level.degree;
  let high = n + 1;
  let middle = Math.floor((low + high) / 2);
  while (u < level.knots[middle] || u >= level.knots[middle + 1]) {
    if (u < level.knots[middle]) high = middle;
    else low = middle;
    middle = Math.floor((low + high) / 2);
  }
  return middle;
}

function deBoor(level: CurveLevel, u: number): Vec3 {
  if (level.degree === 0)
    return level.points[Math.min(level.points.length - 1, findSpan(level, u))];
  const span = findSpan(level, u);
  const values = Array.from(
    { length: level.degree + 1 },
    (_, index) => level.points[span - level.degree + index],
  );
  for (let order = 1; order <= level.degree; order += 1) {
    for (let index = level.degree; index >= order; index -= 1) {
      const pointIndex = span - level.degree + index;
      const denominator =
        level.knots[pointIndex + level.degree - order + 1] - level.knots[pointIndex];
      const alpha =
        Math.abs(denominator) > Number.EPSILON ? (u - level.knots[pointIndex]) / denominator : 0;
      values[index] = add(scale(values[index - 1], 1 - alpha), scale(values[index], alpha));
    }
  }
  return values[level.degree];
}

function derivativeLevel(source: CurveLevel): CurveLevel {
  if (source.degree <= 0 || source.points.length < 2)
    return { points: [vec()], knots: [source.knots[0], source.knots.at(-1) ?? 1], degree: 0 };
  const points: Vec3[] = [];
  for (let index = 0; index < source.points.length - 1; index += 1) {
    const denominator = source.knots[index + source.degree + 1] - source.knots[index + 1];
    points.push(
      Math.abs(denominator) > Number.EPSILON
        ? scale(sub(source.points[index + 1], source.points[index]), source.degree / denominator)
        : vec(),
    );
  }
  return { points, knots: source.knots.slice(1, -1), degree: source.degree - 1 };
}

export class CubicBSpline {
  readonly closed: boolean;
  readonly controlPointCount: number;
  readonly spans: readonly BSplineSpan[];
  private readonly curve: CurveLevel;
  private readonly firstDerivative: CurveLevel;
  private readonly secondDerivative: CurveLevel;
  private readonly domainStart: number;
  private readonly domainEnd: number;

  constructor(definition: BSplineDefinition) {
    const degree = definition.degree ?? 3;
    if (degree !== 3)
      throw new TrackBuildError(
        'unsupported_degree',
        'Buncha Coasters V1 requires degree-3 B-splines.',
      );
    if (definition.controlPoints.length < degree + 1)
      throw new TrackBuildError(
        'too_few_control_points',
        'A cubic B-spline requires at least four control points.',
      );
    definition.controlPoints.forEach((point, index) => {
      if (!finite(point))
        throw new TrackBuildError(
          'non_finite_control_point',
          `Control point ${index} is not finite.`,
          { controlPointIndex: index },
        );
    });
    this.closed = definition.closed ?? false;
    this.controlPointCount = definition.controlPoints.length;
    if (this.closed && definition.knots)
      throw new TrackBuildError(
        'closed_custom_knots_unsupported',
        'Closed V1 curves use the authoritative periodic knot policy.',
      );
    const points = this.closed
      ? [...definition.controlPoints, ...definition.controlPoints.slice(0, degree)]
      : [...definition.controlPoints];
    const knots = definition.knots
      ? [...definition.knots]
      : this.closed
        ? periodicUniformKnots(points.length, degree)
        : openUniformKnots(points.length, degree);
    validateKnots(knots, points.length, degree);
    this.curve = { points, knots, degree };
    this.firstDerivative = derivativeLevel(this.curve);
    this.secondDerivative = derivativeLevel(this.firstDerivative);
    this.domainStart = knots[degree];
    this.domainEnd = knots[points.length];
    const domainLength = this.domainEnd - this.domainStart;
    const spans: BSplineSpan[] = [];
    for (let nativeSpan = degree; nativeSpan < points.length; nativeSpan += 1) {
      if (!(knots[nativeSpan + 1] > knots[nativeSpan])) continue;
      const controlPointIndices = Array.from({ length: degree + 1 }, (_, offset) => {
        const augmentedIndex = nativeSpan - degree + offset;
        return this.closed ? augmentedIndex % this.controlPointCount : augmentedIndex;
      });
      spans.push({
        index: spans.length,
        uStart: (knots[nativeSpan] - this.domainStart) / domainLength,
        uEnd: (knots[nativeSpan + 1] - this.domainStart) / domainLength,
        controlPointIndices,
      });
    }
    this.spans = spans;
  }

  evaluate(normalizedU: number): BSplineEvaluation {
    if (!Number.isFinite(normalizedU))
      throw new TrackBuildError('non_finite_parameter', 'Curve parameter must be finite.');
    const clamped = Math.min(1, Math.max(0, normalizedU));
    const nativeScale = this.domainEnd - this.domainStart;
    const nativeU = this.domainStart + nativeScale * clamped;
    return {
      position: deBoor(this.curve, nativeU),
      derivative: scale(deBoor(this.firstDerivative, nativeU), nativeScale),
      secondDerivative: scale(deBoor(this.secondDerivative, nativeU), nativeScale * nativeScale),
    };
  }
}
